import { useState, useCallback } from 'react'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useVoiceClient } from './useVoiceClient'
import { AgentTypeSelector } from './AgentTypeSelector'
import { VapiConnectionControls } from './VapiConnectionControls'
import { VapiConversationPanel } from './VapiConversationPanel'
import { VapiEventLogPanel } from './VapiEventLogPanel'

const PROVIDER_LABEL: Record<'vapi' | 'retell', string> = {
  vapi: 'VAPI',
  retell: 'Retell',
}

const PROVIDER_BADGE_CLASS: Record<'vapi' | 'retell', string> = {
  // Match the existing purple-VAPI accent.
  vapi: 'bg-purple-500/10 text-purple-400',
  // Distinct accent so HIPAA / Retell calls are visually distinguishable.
  retell: 'bg-emerald-500/10 text-emerald-400',
}

export function VapiVoiceTestConsole() {
  const { t } = useTranslation('calls')
  const {
    status,
    provider,
    agentType,
    setAgentType,
    messages,
    events,
    isSpeaking,
    connect,
    disconnect,
  } = useVoiceClient()

  const [error, setError] = useState<string | null>(null)

  const handleConnect = useCallback(async () => {
    setError(null)
    try {
      await connect()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [connect])

  const handleDisconnect = useCallback(async () => {
    setError(null)
    try {
      await disconnect()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [disconnect])

  const isConnected = status === 'connected'

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Top bar: agent type + connect controls */}
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface p-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
        <div className="flex items-center gap-3">
          <span
            className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
              provider ? PROVIDER_BADGE_CLASS[provider] : 'bg-surface-elevated text-text-muted'
            }`}
          >
            {provider ? PROVIDER_LABEL[provider] : 'Voice'}
          </span>
          <AgentTypeSelector
            value={agentType}
            onChange={setAgentType}
            disabled={isConnected}
          />
        </div>
        <VapiConnectionControls
          status={status}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-error/30 bg-error/10 px-4 py-2.5 text-sm text-error">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="flex-1 break-words">{error}</span>
          <button onClick={() => setError(null)} className="shrink-0 text-xs underline hover:no-underline">
            {t('voiceTest.dismiss')}
          </button>
        </div>
      )}

      {/* Speaking indicator */}
      <div className="flex items-center gap-3 rounded-xl border border-border bg-surface p-4">
        <div className={`h-3 w-3 rounded-full transition-colors ${isSpeaking ? 'bg-success animate-pulse' : isConnected ? 'bg-text-muted' : 'bg-surface-elevated'}`} />
        <span className="text-sm text-text-secondary">
          {isSpeaking ? t('voiceTest.speaking') : isConnected ? t('voiceTest.listening') : t('voiceTest.audioVisualization')}
        </span>
        {isConnected && (
          <span className="ml-auto text-xs text-text-muted">
            {t('voiceTest.directConnection')}
          </span>
        )}
      </div>

      {/* Conversation + Event log (fill remaining height) */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
        <VapiConversationPanel messages={messages} />
        <VapiEventLogPanel events={events} />
      </div>
    </div>
  )
}
